<template>
  <div>
    <div class="col-12 p0 py2 my1 break-word">
      <h2 class="m0 caps">
        Séance
      </h2>
    </div>

    <div class="flex flex-wrap">
      <div class="col-12 center">
        <h2 class="capitalize">{{ dayLabel }}</h2>
      </div>
      <div v-for="(session, index) in sessionsInDate" :key="index" class="col-12 flex justify-center">
        <div class="session-box white-bg shadow flex flex-wrap pointer col-6 mb2 sm-col-12 md-col-8 lg-col-8" @click="handleClick(index)">
          <div :class="['col-2 flex items-center center px2 inline-block', { 'light-blue-bg white': isSessionSelected(index) }]">
            <i class="h1 line-height-0 icon-clock"></i>
          </div>
          <div class="flex col-10 justify-between">
            <div :class="['col-12 ml1', {'col-5': !isInternalSession}]">
              <div class="col-12">
                <h2 class="bold my0">{{ session.label }}</h2>
              </div>
              <div class="col-12">
                <span class="h5">{{ session.activity.name }}</span>
              </div>
            </div>
            <div v-if="isInternalSession" class="col-5 content-center">
              <div class="col-12">
                <h2 class="bold my0 h4">Stock: </h2>
              </div>
              <div class="col-12">
                <span class="h5">{ {{ stockLeft(session) }} / {{ session.maxParticipants || '-' }} }</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div ref="sessionButton" class="col-12 py3 flex justify-end items-center">
      <Button class="col-4 sm-col-6" :action="handleBack" text="Precedent" color="grey-bg" />
      <Button v-if="!loading" class="col-4 sm-col-6 ml2" :disabled="selectedSession === null" :action="confirmSession" text="Suivant" />
      <Button v-else class="col-4 sm-col-6 ml2" :disabled="true" icon="icon icon-loader" />
    </div>
    <CustomerHorsesModal
      v-if="showHorseModal"
      :horses="customerHorses"
      @hide-popup="showHorseModal = false"
      @horses-selected="handleHorseSelected"
      @is-selected-horses="handleIsSelectedHorses"
    />
  </div>
</template>

<script>
import { format, isSameDay } from 'date-fns';
import { fr } from 'date-fns/locale/fr';
import Button from '@/components/elements/Button';
import CustomerHorsesModal from '@/components/pegase/CustomerHorsesModal.vue';

import { submitShoppingCart, getSessionForDate, validateSessionReservation, getCustomerHorses } from '@/utils/requests';
import { changeToPage, validatePreReservationParticipants } from '@/utils/logics';
import handleSubscription from '@/utils/amplify';

export default {
  components: { Button, CustomerHorsesModal },
  data () {
    return {
      selectedDate: new Date(this.$store.state.cartItem.selectedDate),
      selectedSession: null,
      loading: false,
      offerInfo: this.$store.state.offerInfo,
      customerHorses: null,
      selectedHorses: {},
      showHorseModal: false,
      isSelectedHorses: false
    };
  },
  computed: {
    siteInfo () {
      return this.$store.state.siteInfo;
    },
    isAmplifyWorkspace () {
      return this.isInternalSession || ['postgres'].includes(this.siteInfo.horanetSource);
    },
    sessions () {
      return this.$store.state.sessions || { items: [] };
    },
    activities () {
      return this.$store.state.activities;
    },
    productServices () {
      return this.$store.state.productServices;
    },
    dayLabel () {
      return format(this.selectedDate, 'EEEE dd LLLL', { locale: fr });
    },
    isOffer1D () {
      return this.offerInfo && this.offerInfo.detail && this.offerInfo.detail.stage1D;
    },
    isImpersonateMode () {
      return this.$store.getters.isImpersonateMode;
    },
    sessionsInDate () {
      const sessionsToShow = [...this.sessions.items];
      // Verify if the session date belongs to the selected date
      const sessions = [];

      sessionsToShow.map(session => {
        if (!isSameDay(new Date(session.start_date), this.selectedDate)) return;

        if (!this.isOffer1D || !session.recurring) {
          sessions.push(session);
          return;
        }

        const isRecurringPushed = sessions.some(s => s.recurring && s.recurring_uuid === session.recurring_uuid);

        if (isRecurringPushed) return;

        const sameRecurringSessions = sessionsToShow.filter(s => s.recurring_uuid === session.recurring_uuid).sort((a, b) => a.start_date - b.start_date);

        if (!this.isImpersonateMode && sameRecurringSessions.some(s => {
          const stock = this.stockLeft(s);
          return stock === '-' || stock < 1;
        })) return;

        const maxParticipants = Math.max(...sameRecurringSessions.map(s => s.participantsAmount));

        sessions.push({ ...sameRecurringSessions[0], recurringParticipantsAmount: maxParticipants });
      });
      return sessions.sort((a, b) => {
        if (this.isInternalSession && a.start_date !== b.start_date) {
          return a.start_date - b.start_date;
        }
        return a.activity.name.localeCompare(b.activity.name);
      });
    },
    isInternalSession () {
      return this.$store.state.siteInfo && this.$store.state.siteInfo.is_internal_session;
    },
    horseOwnershipValid () {
      return this.isInternalSession;
    }
  },
  async created () {
    if (this.isAmplifyWorkspace) {
      this.$store.commit('setSessions', []);
      this.$store.commit('setLoading', true);
      const variables = {
        date: format(this.selectedDate, 'yyyy-MM-dd'),
        workspace: this.siteInfo.workspace
      };

      if (!this.isInternalSession && ['postgres'].includes(this.siteInfo.horanetSource)) {
        variables.codeSiteComptage = this.siteInfo.code_site_comptage;
      }

      if (this.isInternalSession) {
        variables.productUuid = this.$store.state.product.uuid;
      }

      const response = await getSessionForDate(variables);
      this.$store.commit('setSessions', response.sessions || []);
      this.$store.commit('setLoading', false);
    }
    if (this.isAmplifyWorkspace && !this.$store.state.amplifySubscription) {
      await handleSubscription();
    }

    // if we are on stage 1D we choose the first session
    if (this.isOffer1D) {
      if (this.sessionsInDate.length) {
        // there only select if is for same session
        const isSameSession = this.sessionsInDate.every(s => {
          if (s.hasOwnProperty('recurring')) {
            if (!s.recurring) {
              return false;
            }

            return s.recurring_uuid === this.sessionsInDate[0].recurring_uuid;
          }
          return true;
        });

        if (isSameSession) {
          this.selectedSession = 0;

          this.confirmSession();
        }
      }
    }
  },
  methods: {
    changeToPage,
    isSessionSelected (index) {
      return this.selectedSession === index;
    },
    handleClick (index) {
      this.selectedSession = index;
      const element = this.$refs.sessionButton;
      element.scrollIntoView({ behavior: 'smooth' });
    },
    async validateHorseOwnership () {
      const { cartItem, user } = this.$store.state;
      this.customerHorses = user?.horses || null;

      if (!this.customerHorses) {
        const participantsUuids = (cartItem.participants || []).map(participant => participant.uuid);
        const promises = participantsUuids.map(uuid => getCustomerHorses(uuid));
        const horses = await Promise.all(promises).catch(e => this.$store.commit('setErrorMessage', e.message));

        this.customerHorses = horses.flat();
        this.$store.commit('setUser', { ...user, horses: this.customerHorses });
      }

      if (this.customerHorses && this.customerHorses.length && (cartItem.participants.length > 1 || this.customerHorses.length > 1)) {
        this.showHorseModal = true;
        this.loading = false;
        return false;
      }

      if (this.customerHorses && this.customerHorses.length === 1 && cartItem.participants.length === 1) {
        this.selectedHorses = { [cartItem.participants[0].uuid]: this.customerHorses[0].uuid };
        this.isSelectedHorses = true;
      }

      return true;
    },

    async confirmSession () {
      const { cartItem } = this.$store.state;
      const session = this.sessionsInDate.find((s, index) => index === this.selectedSession);
      if (!session) {
        return;
      }

      cartItem.session = {
        name: session.activity.name,
        code: session.code,
        start_time: session.start_date,
        end_time: session.end_date,
        last_time: session.last_time
      };

      this.$store.commit('setCartItem', { ...cartItem });
      this.loading = true;
      this.$store.commit('setErrorMessage', null);

      const { preReservationOrder } = this.$store.state;
      if (preReservationOrder && preReservationOrder.orderGeneric && !preReservationOrder.session) {
        const orderItems = preReservationOrder.orderGeneric.order?.order_items || [];
        const participants = cartItem.participants || [];

        if (session && participants.length > 0) {
          const { matchCount, allMatch } = await validatePreReservationParticipants(participants, orderItems, { ...session, id: session.id || session.code });

          if (matchCount > 0) {
            this.$store.commit('setPreReservationOrder', { ...preReservationOrder, excludeResumeOrder: !allMatch });
            this.$store.commit('setPage', 'Session');
            this.changeToPage('ResumeOrder');
            this.loading = false;
            return;
          }
        }
      }

      if (this.isAmplifyWorkspace) {
        // validate if can reserve
        const response = await validateSessionReservation(session);

        if (!response || (response && !response.isValid)) {
          this.$store.commit('setErrorMessage', response.message);
          return;
        }
      }

      if (this.horseOwnershipValid && !this.isSelectedHorses) {
        const canContinue = await this.validateHorseOwnership();
        if (!canContinue) {
          return;
        }
      }

      if (this.isSelectedHorses) {
        cartItem.participants = cartItem.participants.map(participant => {
          const horse = this.selectedHorses[participant.uuid] || null;

          return horse ? { ...participant, horse: horse.uuid, horseName: horse.name } : participant;
        });

        this.$store.commit('setCartItem', { ...cartItem });
      }

      await submitShoppingCart()
        .then(response => {
          const { addShoppingCartV3 } = response;
          if (addShoppingCartV3 && addShoppingCartV3.success) {
            const { shoppingCart } = addShoppingCartV3;
            this.$store.commit('addCartItemToCart', shoppingCart);
            this.changeToPage('CartRecap');
          } else if (addShoppingCartV3.error) {
            this.$store.commit('setErrorMessage', addShoppingCartV3.message);
          }
        }).finally(() => {
          if (this.$store.state.errorMessage) {
            const element = document.getElementById('errorBlock');
            if (element) {
              element.scrollIntoView({ behavior: 'smooth' });
            }
          }
          this.loading = false;
        });
    },
    handleBack () {
      this.changeToPage('Calendar');
    },
    stockLeft (session) {
      let availableSlots = 0;
      const capacity = session?.detail?.capacity !== null ? session.detail.capacity : session.maxParticipants;
      if (capacity) {
        availableSlots = capacity - (session.recurringParticipantsAmount || session.participantsAmount);
        return availableSlots >= 0 ? availableSlots : 0;
      }
      return '-';
    },
    handleHorseSelected (horsesSelection) {
      this.selectedHorses = horsesSelection;
      this.showHorseModal = false;
      this.confirmSession();
    },
    handleIsSelectedHorses (isSelectedHorses) {
      this.isSelectedHorses = isSelectedHorses;
    }
  }
};
</script>

<style lang="scss" scoped>
  .shadow {
    box-shadow: 0px 2px 5px 1px #ddd;
  }
  .line-height-0 {
    line-height: 0;
  }

</style>
